import { useEffect, useRef, useState } from "react";
import { CloudArrowUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import { formatDate, humanize } from "@/lib/format";

const COLS = ["refs", "copied", "verified", "missing_source", "errors"];

// Migration: start/resume (409 r2_not_enabled), then poll GET /admin/storage/migrate every 3s while running.
export default function MigrationPanel({ initial, r2 }) {
  const [m, setM] = useState(initial);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const running = !!(m?.running || m?.state === "running");

  useEffect(() => {
    if (!running) return undefined;
    timer.current = setInterval(async () => {
      try { setM(await api("/admin/storage/migrate")); } catch (e) { setError(e.message); }
    }, 3000);
    return () => clearInterval(timer.current);
  }, [running]);

  const start = async () => {
    setBusy(true); setError(null);
    try { await api("/admin/storage/migrate", { method: "POST" }); setM(await api("/admin/storage/migrate")); }
    catch (e) { setError(e.code === "r2_not_enabled" ? "R2 isn't enabled here (missing keys or the bucket guard refused it), so nothing can be migrated." : e.message); }
    finally { setBusy(false); }
  };

  const counts = m?.counts || {};
  return (
    <Panel className="p-6" data-testid="storage-migration-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="font-semibold">Migration to R2</h2>
          <p className="cw-soft mt-1 text-sm" data-testid="storage-migration-state">
            {m ? <>{humanize(m.state || "never_run")}{m.started_at ? ` · started ${formatDate(m.started_at)}` : ""}{m.finished_at ? ` · finished ${formatDate(m.finished_at)}` : ""}</> : "Never run"}
            {running && <Pill tone="brand" className="ml-2">Updating every 3s</Pill>}</p></div>
        <Button variant="outline" onClick={start} disabled={busy || running || !r2} title={!r2 ? "R2 isn't enabled" : undefined} data-testid="storage-migrate-button"><CloudArrowUp />{running ? "Migrating…" : "Migrate media to R2"}</Button>
      </div>
      {error && <p className="cw-error mt-3 text-sm" role="alert" data-testid="storage-migrate-error">{error}</p>}
      {Object.keys(counts).length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm" data-testid="storage-migration-table">
            <thead><tr className="cw-muted border-b border-border text-left text-xs"><th className="py-2 pr-4 font-medium">Type</th>{COLS.map((c) => <th key={c} className="py-2 pr-4 text-right font-medium">{humanize(c)}</th>)}</tr></thead>
            <tbody>{Object.entries(counts).map(([t, c]) => (
              <tr key={t} className="border-b border-border last:border-0"><td className="py-2 pr-4">{humanize(t)}</td>
                {COLS.map((k) => <td key={k} className={`py-2 pr-4 text-right tabular-nums ${(k === "errors" || k === "missing_source") && c[k] ? "cw-error font-medium" : ""}`}>{c[k] ?? 0}</td>)}</tr>))}</tbody>
          </table>
        </div>)}
    </Panel>
  );
}
